import { motion } from "framer-motion";
import {
  Sun,
  BatteryCharging,
  PlugZap,
  CarFront,
  Wrench,
  Frame,
  Building,
  TreePine,
  Layers,
  Flame,
  ShieldCheck,
  Award,
  ArrowUpRight,
  Handshake,
  Route,
  BadgeCheck,
  MapPin,
  FileCheck2,
  Users,
} from "lucide-react";
import SEO from "@/components/SEO";
import TrustMarquee from "@/components/TrustMarquee";
import { FadeUp, Chapter, CTABand, EASE } from "@/components/Reveal";

const PARTNERS = [
  {
    id: "gs-solar",
    naam: "GS Solar",
    monogram: "GS",
    website: "https://gssolar.nl",
    domein: "gssolar.nl",
    tagline: "Standaard de groenste keuze voor uw dak",
    beschrijving:
      "GS Solar realiseert zonnepanelen, batterijen, laadpunten en solar overkappingen voor woningen en bedrijven, vanuit Middelburg. Met persoonlijk advies, heldere afspraken en een strak afgewerkte installatie — voor particulieren in Zeeland en voor zakelijke projecten ook in Zuid-Holland.",
    diensten: [
      { icon: Sun, label: "Zonnepanelen" },
      { icon: BatteryCharging, label: "Thuisbatterijen" },
      { icon: PlugZap, label: "Laadpalen" },
      { icon: CarFront, label: "Solar carports" },
      { icon: Wrench, label: "Onderhoud & service" },
    ],
    kenmerken: [
      { icon: Award, text: "InstallQ-erkend voor zonnestroomsystemen" },
      { icon: ShieldCheck, text: "Tot 10 jaar installatiegarantie" },
      { icon: BadgeCheck, text: "Persoonlijk advies en één aanspreekpunt" },
    ],
    image: "https://gssolar.nl/assets/images/hero-zonnepanelen-woonhuis.jpg",
    imageAlt: "Zonnepanelen op een woning, geïnstalleerd door GS Solar",
  },
  {
    id: "gs-duurzaam",
    naam: "GS Duurzaam",
    monogram: "GD",
    website: "https://gsduurzaam.nl",
    domein: "gsduurzaam.nl",
    tagline: "Nieuwe kozijnen. Meer ruimte. Eén aanspreekpunt.",
    beschrijving:
      "GS Duurzaam coördineert verduurzings- en verbouwprojecten vanuit Middelburg: van kozijnen, deuren en schuifpuien tot aanbouwen, tuinhuizen op maat, isolatie en warmtepompen. Met één aanspreekpunt, een heldere scope en vakmensen die de uitvoering netjes afronden — in Zeeland, Noord-Brabant en Zuid-Holland.",
    diensten: [
      { icon: Frame, label: "Kozijnen & schuifpuien" },
      { icon: Building, label: "Aanbouw & uitbouw" },
      { icon: TreePine, label: "Tuinhuizen op maat" },
      { icon: Layers, label: "Woningisolatie" },
      { icon: Flame, label: "Warmtepompen" },
    ],
    kenmerken: [
      { icon: Users, text: "Eén aanspreekpunt, van opname tot oplevering" },
      { icon: FileCheck2, text: "Heldere scope: vooraf vastgelegd wat inbegrepen is" },
      { icon: MapPin, text: "Actief in Zeeland, Noord-Brabant en Zuid-Holland" },
    ],
    image: "https://gsduurzaam.nl/assets/images/hero-duurzame-woning.jpg",
    imageAlt: "Moderne duurzame woning met nieuwe kozijnen, project van GS Duurzaam",
  },
];

const SAMENWERKING = [
  {
    icon: Route,
    title: "Van inzicht naar actie",
    text: "Directelabels brengt de energieprestatie van uw woning zorgvuldig in kaart. Onze partners helpen u vervolgens om die prestatie daadwerkelijk te verbeteren.",
  },
  {
    icon: Handshake,
    title: "Eén duidelijk traject",
    text: "Uw energielabel laat zien waar de winst zit. Wilt u verder met zonnepanelen, isolatie of nieuwe kozijnen, dan verwijzen wij u rechtstreeks door naar de juiste specialist.",
  },
  {
    icon: ShieldCheck,
    title: "Vakwerk en garantie",
    text: "Wij werken alleen met partners die dezelfde zorgvuldigheid hanteren als wij: heldere afspraken, erkende vakmensen en duidelijke garanties.",
  },
];

function PartnerCard({ p, flip }) {
  return (
    <FadeUp>
      <div className="overflow-hidden rounded-3xl border border-black/5 bg-white shadow-sm" data-testid={`partner-card-${p.id}`}>
        <div className="grid lg:grid-cols-12">
          <div className={`p-8 md:p-12 lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
            <span className="inline-flex items-center gap-2.5" data-testid={`partner-logo-${p.id}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary font-display text-lg font-bold text-white">
                {p.monogram}
              </span>
              <span className="font-display text-2xl font-semibold tracking-tight text-ink">{p.naam}</span>
            </span>
            <h2 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink md:text-3xl">{p.tagline}</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{p.beschrijving}</p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {p.diensten.map((d) => (
                <span
                  key={d.label}
                  data-testid={`partner-dienst-${p.id}-${d.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-energy-pale px-4 py-2 text-xs font-bold text-energy-deep"
                >
                  <d.icon size={14} />
                  {d.label}
                </span>
              ))}
            </div>
            <ul className="mt-7 space-y-3">
              {p.kenmerken.map((k) => (
                <li key={k.text} className="flex items-start gap-3 text-sm font-semibold text-ink/80">
                  <k.icon size={17} className="mt-0.5 shrink-0 text-primary" />
                  {k.text}
                </li>
              ))}
            </ul>
            <a
              href={p.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`partner-visit-${p.id}`}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-energy-light"
            >
              Bezoek {p.domein}
              <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
          <div className={`relative min-h-64 lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
            <img
              src={p.image}
              alt={p.imageAlt}
              className="h-full w-full object-cover"
              data-testid={`partner-image-${p.id}`}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </FadeUp>
  );
}

export default function Partners() {
  return (
    <>
      <SEO
        title="Onze partners | GS Solar & GS Duurzaam | Directelabels"
        description="Directelabels werkt samen met GS Solar (zonnepanelen, thuisbatterijen, laadpalen) en GS Duurzaam (kozijnen, aanbouw, isolatie, warmtepompen). Van energielabel naar een energiezuinige woning."
        path="/partners"
      />

      <section data-testid="partners-hero" className="mx-auto max-w-7xl px-5 pb-16 pt-14 md:px-8 md:pt-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs font-bold uppercase tracking-[0.24em] text-primary"
        >
          Onze partners
        </motion.p>
        <h1 data-testid="partners-title" className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tighter text-ink md:text-6xl">
          {["Van energielabel", "naar energiezuinig wonen."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {i === 1 ? <span className="text-primary">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg"
        >
          Een energielabel is het begin van een energiezuinige woning. Daarom werkt Directelabels samen met twee
          specialisten: GS Solar voor zonnepanelen, thuisbatterijen en laadpalen, en GS Duurzaam voor kozijnen,
          isolatie en verbouwingen.
        </motion.p>
      </section>

      <TrustMarquee />

      <section data-testid="partner-card-section" className="mx-auto max-w-7xl space-y-10 px-5 py-24 md:px-8 md:py-32">
        {PARTNERS.map((p, i) => (
          <PartnerCard key={p.id} p={p} flip={i % 2 === 1} />
        ))}
      </section>

      <section data-testid="partner-samenwerking" className="border-y border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <Chapter
            number="01"
            label="Waarom deze samenwerking"
            title="Eén gedachte: uw woning energiezuiniger maken"
            description="Wij meten en registreren, onze partners realiseren. Zo krijgt u een logisch vervolg op uw energielabel, zonder omweg."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SAMENWERKING.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.1}>
                <div
                  data-testid={`partner-samenwerking-${i}`}
                  className="group h-full rounded-3xl border border-black/5 bg-background p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-energy-pale text-primary transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                    <s.icon size={22} />
                  </span>
                  <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
